import Avatar from '-/Custom/Avatar';
import type { TeamSelectItemArgs } from '-/Team/TeamSelect.types';
import type { PropsWithChildren, ReactElement } from 'react';
import { useQuery } from 'urql';
import Text from '@/components/UI/Text';
import { graphql, type ResultOf, readFragment } from '@/gql/graphql';
import getAvatarText from '@/utils/getAvatarText';
import Box from '../UI/Box';
import CheckboxItem from '../UI/CheckboxRow';
import List from '../Unistyles/List';

type TeamSelectProps = PropsWithChildren<{
  where: ReturnType<typeof graphql.scalar<'team_bool_exp'>>;
  search: string;
  extraData?: unknown;
  onPressItem?(args: TeamSelectItemArgs): void;
  filteredIds?: string[];
  isChecked?(id: string): boolean;
  isDisabled?(id: string): boolean;
  renderItem?(item: ResultOf<typeof TeamSelectFragment>): ReactElement;
}>;

const PAGE_SIZE = 25;
const SEARCH_INPUT_GATE = 3;
const EMPTY_FILTERED_IDS: string[] = [];

export const TeamSelectFragment = graphql(/* GraphQL */ `
  fragment TeamSelect on team {
    id
    title
    home_primary_color
    avatar_id
  }
`);

const SearchTeamSelect = graphql(/* GraphQL */ `
  query SearchTeamSelect($where: team_bool_exp!, $search: String!) {
    search_team(
      order_by: { title: asc }
      args: { search: $search }
      where: $where
    ) {
      ...TeamSelect
    }
  }
`, [TeamSelectFragment]);

const GetTeamSelect = graphql(/* GraphQL */ `
  query GetTeamSelect(
    $where: team_bool_exp!
    $filteredIds: [uuid!]
    $limit: Int!
  ) {
    team(
      where: { _and: [{ id: { _nin: $filteredIds } }, $where] }
      limit: $limit
      order_by: { title: asc }
    ) {
      ...TeamSelect
    }
  }
`, [TeamSelectFragment]);

export default function TeamSelect({
  where,
  search,
  extraData,
  onPressItem,
  filteredIds = EMPTY_FILTERED_IDS,
  isChecked,
  isDisabled,
  renderItem,
}: TeamSelectProps) {
  const [{ data: searchData, fetching: searchFetching }] = useQuery({
    query: SearchTeamSelect,
    variables: { where, search },
    requestPolicy: 'cache-and-network',
    pause: search.length < SEARCH_INPUT_GATE,
  });

  const [{ data: getData, fetching: getFetching }] = useQuery({
    query: GetTeamSelect,
    variables: { where, filteredIds, limit: PAGE_SIZE },
    requestPolicy: 'cache-and-network',
  });

  const searchTeams = searchData?.search_team || [];
  const getTeams = getData?.team || [];
  const fragment = search.length >= SEARCH_INPUT_GATE ? searchTeams : getTeams;
  const data = readFragment(TeamSelectFragment, fragment);
  const filteredData = data.filter((item) => !filteredIds.includes(item.id));
  const fetching =
    search.length >= SEARCH_INPUT_GATE ? searchFetching : getFetching;

  const defaultRenderItem = (item: ResultOf<typeof TeamSelectFragment>) => (
    <CheckboxItem
      aria-label={item.title || 'Unknown Team'}
      value={!!isChecked?.(item.id)}
      onValueChange={() => {
        if (onPressItem) {
          onPressItem({
            id: item.id,
            title: item.title,
            home_primary_color: item.home_primary_color,
            avatar_id: item.avatar_id,
          });
        }
      }}
      disabled={isDisabled?.(item.id)}
      variant="primary"
    >
      <Box direction="horizontal" flex space="lg" alignItems="center">
        <Avatar
          color={item.home_primary_color}
          id={item.avatar_id || ''}
          name={getAvatarText(item.title)}
          size="md"
        />
        <Text flexShrink={1} numberOfLines={1} size="xl">
          {item.title}
        </Text>
      </Box>
    </CheckboxItem>
  );

  return (
    <List
      data={filteredData}
      extraData={extraData}
      fetching={fetching}
      ListEmptyComponent={
        <Box p="lg">
          <Text center variant="typographySecondary">
            No teams found.
          </Text>
        </Box>
      }
      renderItem={({ item }) =>
        renderItem ? renderItem(item) : defaultRenderItem(item)
      }
    />
  );
}
